import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';

import React, { useEffect, useMemo, memo } from 'react';

import { Box, Text as BokehText, Tooltip } from '@vimeo/bokeh';

import { T } from '../../translations';
import { convertDateTime } from '../../utils';
import MentionExtension from '../Mention/MentionExtension';
import SearchHighlightExtension from '../SearchComments/SearchHighlightExtension';

type Props = {
  text?: string;
  editedTime?: string;
  isResolved?: boolean;
  searchQuery?: string;
};

export const RichCommentRender = ({ text = '', editedTime, isResolved, searchQuery }: Props) => {
  const richContent = useMemo(() => {
    try {
      return JSON.parse(text);
    } catch (_e) {
      return;
    }
  }, [text]);

  const editor = useEditor({
    editable: false,
    extensions: [
      Document,
      Text,
      Paragraph,
      Bold,
      HardBreak,
      SearchHighlightExtension.configure({
        searchHighlightClass: 'highlighted-text',
      }),
      MentionExtension,
    ],
    content: richContent,
  });

  useEffect(() => {
    if (richContent) {
      setTimeout(() => {
        editor?.chain().setContent(richContent).run();
      }, 0);
    }
  }, [richContent, editor]);

  useEffect(() => {
    if (editor) {
      editor.commands.setSearchTerm(searchQuery || '');
    }
  }, [searchQuery, editor]);

  useEffect(() => {
    if (editor) {
      const mentionedResults = editor.storage.searchHighlight?.mentionResults ?? [];
      const transaction = editor.state.tr;
      mentionedResults.forEach((result: { from: number; to: number; pos: number }) => {
        const { from, to, pos } = result;
        const currentNode = transaction.doc.nodeAt(pos);
        transaction.setNodeMarkup(pos, null, { ...currentNode?.attrs, highlight: { from, to } });
      });
      editor.view.dispatch(transaction.setTime(Date.now()));
    }
    // The hook needs "editor" for ref but we don't want this to run everytime it changes
    // We only update mention node highlight here and that should run when the mentioned
    // nodes in the search results are updated.
    // eslint-disable-next-line
  }, [editor?.storage?.searchHighlight?.mentionResults?.length]);

  const defaultRichTextViewerStyles = useMemo(
    () => ({
      '.rich-comment-viewer': {
        display: 'inline',
        fontSize: 'text-sm',
        color: 'text-primary',
      },
      '.ProseMirror': {
        paddingLeft: 0,
        display: 'inline',
        p: {
          display: 'inline',
        },
        '.highlighted-text': {
          backgroundColor: 'orange.100',
        },
        '.highlighted-mention-text': {
          backgroundColor: 'orange.100',
          ...(isResolved
            ? {
                color: 'gray.900',
                opacity: 1,
              }
            : {
                color: 'fill-brand',
              }),
        },
      },
    }),
    [isResolved],
  );

  if (!editor) {
    return null;
  }

  return (
    <Box display="inline" sx={defaultRichTextViewerStyles}>
      <EditorContent className="rich-comment-viewer" editor={editor} />
      <EditedText editedTime={editedTime} />
    </Box>
  );
};

interface EditedTextProps {
  editedTime?: string;
}

const EditedText: React.FC<EditedTextProps> = memo(({ editedTime }) =>
  editedTime ? (
    <Tooltip placement="top" label={convertDateTime(editedTime)}>
      <BokehText
        display="inline"
        color="text-secondary"
        variant="body-md"
      >{` (${T.Edited})`}</BokehText>
    </Tooltip>
  ) : null,
);
